/**
 * Typed confirmation for putting another GAM advertiser's orders on an
 * advertiser page — the ONE rule the Create and Manage modals share, because
 * the page's passcode then opens those full reports, spend included. The
 * backend answers 422 `cross_advertiser_include` until the request carries
 * `confirmCrossAdvertiser: true`; this block gates that flag.
 */

import React from 'react';
import { Button } from '../../ui';
import type { CrossAdvertiserOrder } from '../../../services/advertiserReportsApi';
import { fieldStyle } from './shared';

/** Distinct foreign advertiser names in the 422 payload, blanks dropped. */
export const foreignAdvertiserNames = (orders: readonly CrossAdvertiserOrder[]): string[] =>
  Array.from(new Set(orders.map((o) => o.advertiserName.trim()).filter(Boolean)));

/** True once the admin typed one of the foreign advertiser names (case-insensitive, trimmed). */
export const crossAdvertiserConfirmed = (orders: readonly CrossAdvertiserOrder[], typed: string): boolean => {
  const t = typed.trim().toLowerCase();
  return t !== '' && foreignAdvertiserNames(orders).some((n) => n.toLowerCase() === t);
};

interface CrossAdvertiserConfirmProps {
  orders: CrossAdvertiserOrder[];
  /** The page whose passcode would open the foreign reports. */
  clientLabel: string;
  typed: string;
  onTyped: (value: string) => void;
  onBack: () => void;
  /** Order names for display; falls back to "Order {id}". */
  orderNameById?: ReadonlyMap<string, string>;
}

const CrossAdvertiserConfirm: React.FC<CrossAdvertiserConfirmProps> = ({ orders, clientLabel, typed, onTyped, onBack, orderNameById }) => {
  const names = foreignAdvertiserNames(orders);
  return (
    <div style={{ background: 'var(--warning-soft)', borderRadius: 'var(--radius-md)', padding: '10px 12px', fontSize: 13, display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ fontWeight: 700 }}>These campaigns belong to a different GAM advertiser</div>
      <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12.5 }}>
        {orders.map((o) => (
          <li key={o.gamOrderId}>
            {orderNameById?.get(o.gamOrderId) ?? `Order ${o.gamOrderId}`} <span style={{ color: 'var(--fg-4)' }}>(order {o.gamOrderId})</span> —{' '}
            <strong>{o.advertiserName}</strong>
          </li>
        ))}
      </ul>
      <div style={{ fontSize: 12.5 }}>
        The <strong>{clientLabel}</strong> passcode would open {orders.length === 1 ? 'this' : 'these'} full report
        {orders.length === 1 ? '' : 's'}, spend included. Type the advertiser name to confirm:{' '}
        <code>{names.join('” or “')}</code>
      </div>
      <input style={fieldStyle} value={typed} onChange={(e) => onTyped(e.target.value)} placeholder={names[0]} autoFocus />
      <div>
        <Button size="sm" variant="ghost" onClick={onBack}>Back to editing</Button>
      </div>
    </div>
  );
};

export default CrossAdvertiserConfirm;
